import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { Alert, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import team15 from 'assets/img/team/avatar.webp';
import { editFaq, FaqsItems } from 'api/products/_requests';
import { RootState } from 'redux-state/store';

const ProductCell: ColumnDef<FaqsItems>['cell'] = ({ row: { original } }) => {
  const p = original.product;
  if (!p || typeof p === 'string' || !p.name) return null;

  const name = p.name.length > 46 ? `${p.name.slice(0, 46)}…` : p.name;
  return (
    <Link
      to={`/product-details/${p._id}`}
      className="fw-semibold line-clamp-1 fs--1"
    >
      {name}
    </Link>
  );
};

const CustomerCell: ColumnDef<FaqsItems>['cell'] = ({ row: { original } }) => {
  const u = original.user;
  return (
    <div className="d-flex align-items-center gap-2 fs--1">
      <img
        src={u?.avatar || team15}
        alt={u?.fullName}
        className="rounded-circle"
        style={{ width: 24, height: 24 }}
      />
      <span>{u?.fullName || ''}</span>
    </div>
  );
};

const QuestionCell: ColumnDef<FaqsItems>['cell'] = ({ row: { original } }) => (
  <p className="fs-0 mb-0">{original.question}</p>
);

const AnswerCell: ColumnDef<FaqsItems>['cell'] = ({ row: { original } }) => (
  <p className="fs-0 fw-semibold text-body-highlight mb-0 line-clamp-3">
    {original.answer?.slice(0, 134)}
    {original.answer && original.answer.length > 134 && (
      <>
        {'… '}
        <Link to="#!">See more</Link>
      </>
    )}
  </p>
);

const DateCell: ColumnDef<FaqsItems>['cell'] = ({ row }) =>
  new Date(row.original.createdAt ?? '').toLocaleDateString();

interface Props {
  vendorFaqs?: FaqsItems[];
  vendorId?: string;
}

const EcomFaqsVendor: React.FC<Props> = ({ vendorFaqs = [], vendorId }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<FaqsItems | null>(null);
  const { user } = useSelector((s: RootState) => s.auth);
  const { id } = useParams();
  const vendorDetails = useSelector((state: RootState) => state.user.vendor);
  const isVendor = user?.role === 'vendor';

  const queryClient = useQueryClient();

  const handleEdit = useCallback((faq: FaqsItems) => {
    if (id !== vendorDetails.userId) {
      toast.error('You are not authorised to edit this question');
      return;
    }
    setSelectedFaq(faq);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedFaq?._id) return;
    try {
      await editFaq({ _id: selectedFaq._id, answer: selectedFaq.answer ?? '' });
      toast.success('Answered the question');
      queryClient.invalidateQueries({ queryKey: ['faqs-by-vendor', vendorId] });
      setShowModal(false);
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(err);
    }
  }, [selectedFaq, vendorId, queryClient]);

  /* ----------  MEMO COLUMN ARRAY  ---------- */
  const columns: ColumnDef<FaqsItems>[] = useMemo(() => {
    const common: ColumnDef<FaqsItems>[] = [
      {
        accessorKey: 'product',
        header: () => 'Product',
        cell: ProductCell,
        meta: {
          headerProps: { style: { minWidth: 150, maxWidth: 180 } },
          cellProps: { className: 'pe-3' }
        }
      },
      {
        accessorKey: 'user',
        header: 'Customer',
        cell: CustomerCell,
        meta: {
          headerProps: { style: { minWidth: 130, maxWidth: 160 } }
        }
      },
      {
        accessorKey: 'question',
        header: 'Question',
        cell: QuestionCell,
        meta: {
          headerProps: {
            style: { minWidth: 250, maxWidth: 300, textAlign: 'center' }
          },
          cellProps: { className: 'text-center' }
        }
      }
    ];

    if (!isVendor) return common;

    return [
      ...common,
      {
        accessorKey: 'answer',
        header: 'Answer',
        cell: AnswerCell,
        meta: {
          headerProps: { style: { minWidth: 350, maxWidth: 500 } },
          cellProps: { className: 'pe-7' }
        }
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: DateCell,
        meta: {
          headerProps: { className: 'text-end', style: { width: 120 } },
          cellProps: {
            className: 'text-end text-body-tertiary white-space-nowrap'
          }
        }
      },
      {
        accessorKey: 'action',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            className="btn p-0 border-0 bg-transparent"
            onClick={() => handleEdit(row.original)}
          >
            <FontAwesomeIcon icon={faEdit} className="fs-7 text-primary" />
          </button>
        ),
        meta: {
          headerProps: { className: 'text-end', style: { width: 80 } },
          cellProps: { className: 'text-end' }
        }
      }
    ];
  }, [isVendor, handleEdit]); // <-- recalculates only when role or handler changes

  const table = useAdvanceTable({
    data: vendorFaqs,
    columns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });

  /* ----------  RENDER  ---------- */
  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      {isVendor && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Question Response</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5 className="mb-2">{selectedFaq?.question}</h5>
            <textarea
              className="form-control"
              rows={5}
              value={selectedFaq?.answer ?? ''}
              onChange={e =>
                setSelectedFaq(f => (f ? { ...f, answer: e.target.value } : f))
              }
            />
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default EcomFaqsVendor;
